import Empty from 'constants/empty';
import saveForLaterApi from 'services/api/basket/saveForLater';
import Actions from 'actions/Actions';
import RwdBasketActions from 'actions/RwdBasketActions/RwdBasketActions';
import AddToBasketActions from 'actions/AddToBasketActions';
import basketConstants from 'constants/Basket';
import { DELIVERY_METHOD_TYPES } from 'constants/RwdBasket';
import { SAVE_FOR_LATER_LIST_TYPE, DEFAULT_SHOPPING_LIST_NAME, ACTION_TYPES } from 'constants/saveForLater';

const omitUndefined = (obj = Empty.Object) => {
    return Object.keys(obj).reduce((result, key) => {
        if (obj[key] !== undefined) {
            result[key] = obj[key];
        }

        return result;
    }, {});
};

export { ACTION_TYPES };

function setLoading(loading) {
    return { type: ACTION_TYPES.SET_SAVE_FOR_LATER_LOADING, loading };
}

function setError(error) {
    return { type: ACTION_TYPES.SET_SAVE_FOR_LATER_ERROR, error };
}

function updateLists(lists) {
    return { type: ACTION_TYPES.UPDATE_SAVE_FOR_LATER_LISTS, lists };
}

function updateItems(items) {
    return { type: ACTION_TYPES.UPDATE_SAVE_FOR_LATER_ITEMS, items };
}

function addItemOptimistic(item) {
    return { type: ACTION_TYPES.ADD_SAVE_FOR_LATER_ITEM_OPTIMISTIC, item };
}

function removeItemOptimistic(skuId) {
    return { type: ACTION_TYPES.REMOVE_SAVE_FOR_LATER_ITEM_OPTIMISTIC, skuId };
}

/**
 * Fetch lists for a profile.
 * @param {string|number} profileId
 * @param {object} options optional query params
 * @param {function} cbSuccess optional success callback
 * @param {function} cbError optional error callback
 */
function getSaveForLaterLists(profileId, options = Empty.Object, cbSuccess, cbError) {
    return dispatch => {
        dispatch(setLoading(true));

        return saveForLaterApi
            .getLists(profileId, options)
            .then(listsResponse => {
                const hasListId = listsResponse?.shoppingListId || listsResponse?.id;
                const lists = hasListId ? [listsResponse] : Empty.Array;
                dispatch(updateLists(lists));

                const items = listsResponse?.shoppingListItems || Empty.Array;
                dispatch(updateItems(items));

                dispatch(setLoading(false));

                if (typeof cbSuccess === 'function') {
                    cbSuccess(listsResponse);
                }

                return listsResponse;
            })
            .catch(error => {
                dispatch(setError(error));

                dispatch(setLoading(false));

                if (typeof cbError === 'function') {
                    cbError(error);
                }

                return Promise.reject(error);
            });
    };
}

/**
 * Create a new list.
 */
function createSaveForLaterList(profileId, payload = Empty.Object, cbSuccess, cbError) {
    return dispatch => {
        dispatch(setLoading(true));

        const normalizedPayload = omitUndefined({
            shoppingListType: (payload.shoppingListType || SAVE_FOR_LATER_LIST_TYPE).toLowerCase(),
            shoppingListName: payload.shoppingListName || DEFAULT_SHOPPING_LIST_NAME,
            isDefault: payload.isDefault
        });

        return saveForLaterApi
            .createList(profileId, normalizedPayload)
            .then(createdList => {
                // After creation, refetch lists for carousel rendering
                return dispatch(getSaveForLaterLists(profileId)).then(() => {
                    if (typeof cbSuccess === 'function') {
                        cbSuccess(createdList);
                    }

                    return createdList;
                });
            })
            .catch(error => {
                dispatch(setError(error));

                dispatch(setLoading(false));

                if (typeof cbError === 'function') {
                    cbError(error);
                }

                return Promise.reject(error);
            });
    };
}

/**
 * Create an item in a list. payload should minimally contain { skuId, shoppingListName OR shoppingListId }.
 * Low-level API wrapper - use addItemToSaveForLater for high-level orchestration.
 */
function createSaveForLaterItem(profileId, payload = Empty.Object, cbSuccess, cbError) {
    return dispatch => {
        // Optimistic state update (optional – requires reducer support)
        if (payload?.skuId) {
            dispatch(addItemOptimistic(payload));
        }

        const normalizedPayload = omitUndefined({
            source: payload.source || 'productPage',
            productId: payload.productId,
            skuId: payload.skuId,
            shoppingListId: payload.shoppingListId,
            shoppingListType: (payload.shoppingListType || SAVE_FOR_LATER_LIST_TYPE).toLowerCase()
        });

        return saveForLaterApi
            .addItem(profileId, normalizedPayload)
            .then(addItemResponse => {
                // Refresh lists for carousel rendering
                return dispatch(getSaveForLaterLists(profileId)).then(() => {
                    if (typeof cbSuccess === 'function') {
                        cbSuccess(addItemResponse);
                    }

                    return addItemResponse;
                });
            })
            .catch(error => {
                dispatch(setError(error));

                if (typeof cbError === 'function') {
                    cbError(error);
                }

                return Promise.reject(error);
            });
    };
}

/**
 * Search items across lists using arbitrary payload (e.g. skuIds).
 */
function searchSaveForLaterItems(profileId, payload = Empty.Object, cbSuccess, cbError) {
    return dispatch => {
        dispatch(setLoading(true));

        return saveForLaterApi
            .searchItems(profileId, payload)
            .then(searchResults => {
                const items = searchResults?.items || searchResults;
                dispatch(updateItems(items));
                dispatch(setLoading(false));

                if (typeof cbSuccess === 'function') {
                    cbSuccess(searchResults);
                }

                return searchResults;
            })
            .catch(error => {
                dispatch(setError(error));

                dispatch(setLoading(false));

                if (typeof cbError === 'function') {
                    cbError(error);
                }

                return Promise.reject(error);
            });
    };
}

/**
 * Delete an item from a Save For Later list.
 * Low-level API wrapper - use removeItemFromSaveForLater for high-level orchestration.
 */
function deleteSaveForLaterItem(profileId, payload = Empty.Object, cbSuccess, cbError) {
    return dispatch => {
        const { skuId, productId, shoppingListId, shoppingListType } = payload || Empty.Object;

        if (!skuId || !productId || !shoppingListId) {
            const error = new Error('deleteSaveForLaterItem requires skuId, productId, and shoppingListId.');
            dispatch(setError(error));

            return Promise.reject(error);
        }

        const normalizedPayload = omitUndefined({
            skuId,
            productId,
            shoppingListId,
            shoppingListType: (shoppingListType || SAVE_FOR_LATER_LIST_TYPE).toLowerCase()
        });

        return saveForLaterApi
            .removeItem(profileId, normalizedPayload)
            .then(removeItemResponse => {
                if (typeof cbSuccess === 'function') {
                    cbSuccess(removeItemResponse);
                }

                return removeItemResponse;
            })
            .catch(error => {
                dispatch(setError(error));

                if (typeof cbError === 'function') {
                    cbError(error);
                }

                return Promise.reject(error);
            });
    };
}

/**
 * Ensure a Save For Later list exists for the user.
 * Returns the listId, creating a new list if necessary.
 *
 * @param {string|number} profileId - User profile ID
 * @returns {Promise<string>} - The list ID
 */
function ensureSaveForLaterListExists(profileId) {
    return (dispatch, getState) => {
        const state = getState();
        const existingListId = state.saveForLater?.listId;
        const existingLists = state.saveForLater?.lists || Empty.Array;

        // If we already have a listId in Redux, use it
        if (existingListId) {
            return Promise.resolve(existingListId);
        }

        // If we have lists cached but no listId, check if we fetched recently
        if (existingLists.length > 0) {
            // Lists exist but no listId - try to extract it from the first list
            const fallbackListId = existingLists[0]?.shoppingListId || existingLists[0]?.id;

            if (fallbackListId) {
                return Promise.resolve(fallbackListId);
            }
        }

        // Otherwise, fetch lists with full product details to find or create one
        return dispatch(getSaveForLaterLists(profileId)).then(listsResponse => {
            // Extract listId from the API response (single list object)
            const extractedListId = listsResponse?.shoppingListId || listsResponse?.id;

            if (extractedListId) {
                return extractedListId;
            }

            // Only create a new list if we confirmed the backend has no list
            return dispatch(
                createSaveForLaterList(profileId, {
                    shoppingListType: SAVE_FOR_LATER_LIST_TYPE,
                    shoppingListName: DEFAULT_SHOPPING_LIST_NAME,
                    isDefault: true
                })
            ).then(createdList => {
                // Extract listId from the creation response
                const newListId = createdList?.shoppingListId || createdList?.id;

                if (!newListId) {
                    throw new Error('Failed to create Save For Later list');
                }

                return newListId;
            });
        });
    };
}

/**
 * Add an item to Save For Later with full orchestration:
 * - Handles signed-out users (shows sign-in modal)
 * - Ensures list exists
 * - Optimistic updates
 * - Error handling
 * - Basket refresh after sign-in
 *
 * @param {object} params - { skuId, productId, item }
 * @returns {Promise|null} - Returns null for signed-out flow, Promise for signed-in
 */
function addItemToSaveForLater({ skuId, productId, item }) {
    return (dispatch, getState) => {
        const state = getState();
        const profileId = state.user?.profileId;

        // Handle signed-out users
        if (!profileId) {
            dispatch(
                Actions.showSignInModal({
                    isOpen: true,
                    callback: () => {
                        // After sign-in, refresh basket
                        if (typeof RwdBasketActions?.refreshBasket === 'function') {
                            dispatch(RwdBasketActions.refreshBasket());
                        }

                        // Then retry the add operation
                        dispatch(addItemToSaveForLater({ skuId, productId, item }));
                    }
                })
            );

            return null;
        }

        if (!skuId) {
            const error = new Error('Missing skuId for Save For Later');
            dispatch(setError(error));

            return Promise.reject(error);
        }

        // Optimistic update
        dispatch(addItemOptimistic({ skuId, productId, item }));

        // Ensure list exists, then add item
        return dispatch(ensureSaveForLaterListExists(profileId))
            .then(listId => {
                const payload = {
                    skuId,
                    productId,
                    shoppingListId: listId,
                    shoppingListType: SAVE_FOR_LATER_LIST_TYPE,
                    source: 'basket'
                };

                return dispatch(createSaveForLaterItem(profileId, payload));
            })
            .catch(error => {
                // Remove optimistic update on error
                dispatch(removeItemOptimistic(skuId));
                dispatch(setError(error));

                throw error;
            });
    };
}

/**
 * Remove an item from Save For Later and refresh the lists
 * @param {Object} params
 * @param {Object} params.sku - SKU object to remove
 * @param {string} params.userId - User profile ID
 * @param {string} params.listId - Shopping list ID
 * @returns {Promise}
 */
function removeItemFromSaveForLater({ sku, userId, listId }) {
    return dispatch => {
        if (!sku || !userId || !listId) {
            return Promise.resolve();
        }

        const skuId = sku.skuId || sku.sku?.skuId;
        const productId = sku.productId || sku.sku?.productId;

        if (!skuId || !productId) {
            return Promise.resolve();
        }

        return dispatch(
            deleteSaveForLaterItem(userId, {
                skuId,
                productId,
                shoppingListId: listId,
                shoppingListType: SAVE_FOR_LATER_LIST_TYPE
            })
        )
            .then(() => dispatch(getSaveForLaterLists(userId)))
            .catch(error => {
                Sephora.logger.error('Error removing Save For Later item:', error);
                throw error;
            });
    };
}

/**
 * Close the Save For Later modal
 */
function closeSavedForLaterModal() {
    return dispatch => {
        dispatch(Actions.showSavedForLaterModal({ isOpen: false }));
    };
}

/**
 * Show the Change Method modal for Save For Later flow
 * @param {Object} params
 * @param {Object} params.sku - SKU object
 * @param {number} params.qty - Quantity (default: 1)
 * @param {Function} params.onChangeMethod - Callback when delivery method is selected
 */
function showChangeMethodModalForSaveForLater({ sku, qty = 1, onChangeMethod }) {
    return dispatch => {
        dispatch(
            Actions.showChangeMethodModal({
                isOpen: true,
                item: { sku, qty },
                itemDeliveryMethod: null,
                onChangeMethod
            })
        );
    };
}

/**
 * Move an item from Save For Later to basket
 * - Adds item to basket with selected delivery method
 * - Removes item from Save For Later on success
 * - Closes the Change Method modal
 * @param {Object} params
 * @param {string} params.skuId - SKU ID
 * @param {string} params.productId - Product ID
 * @param {number} params.qty - Quantity (default: 1)
 * @param {string} params.deliveryOption - Delivery option (BOPIS, SAMEDAY, or standard)
 * @param {string} params.userId - User profile ID
 * @param {string} params.listId - Shopping list ID
 * @returns {Promise}
 */
function moveItemToBasketFromSaveForLater({
    skuId, productId, qty = 1, deliveryOption, userId, listId
}) {
    return dispatch => {
        const { BasketType } = basketConstants;

        // Determine basket type based on delivery option
        let basketType = BasketType.Standard;

        if (deliveryOption === DELIVERY_METHOD_TYPES.BOPIS) {
            basketType = BasketType.BOPIS;
        } else if (deliveryOption === DELIVERY_METHOD_TYPES.SAMEDAY) {
            basketType = BasketType.SameDay;
        }

        // Build sku object for addToBasket
        const sku = {
            skuId,
            productId
        };

        // Success callback to remove from Save For Later and close modal
        const successCallback = () => {
            if (userId && listId) {
                dispatch(
                    deleteSaveForLaterItem(userId, {
                        skuId,
                        productId,
                        shoppingListId: listId,
                        shoppingListType: SAVE_FOR_LATER_LIST_TYPE
                    })
                ).then(() => dispatch(getSaveForLaterLists(userId)));
            }

            dispatch(Actions.showChangeMethodModal({ isOpen: false }));
        };

        return dispatch(
            AddToBasketActions.addToBasket(
                sku,
                basketType,
                qty,
                successCallback,
                'save-for-later',
                false,
                {},
                false,
                false,
                '',
                productId,
                false,
                false,
                false,
                null
            )
        ).catch(error => {
            Sephora.logger.error('Error adding to basket from save-for-later:', error);
            throw error;
        });
    };
}

export default {
    // sync
    setLoading,
    setError,
    updateLists,
    updateItems,
    addItemOptimistic,
    removeItemOptimistic,
    // thunks - low-level API wrappers
    getSaveForLaterLists,
    createSaveForLaterList,
    createSaveForLaterItem,
    searchSaveForLaterItems,
    deleteSaveForLaterItem,
    // thunks - high-level orchestration
    ensureSaveForLaterListExists,
    addItemToSaveForLater,
    removeItemFromSaveForLater,
    moveItemToBasketFromSaveForLater,
    closeSavedForLaterModal,
    showChangeMethodModalForSaveForLater
};
